import React from 'react';
import '../VolumeSlider/VolumeSlider.css';
import './ReplayPlaybackPanel.css';
import {useReplayControls} from '../../contexts/ReplayControlsContext';
import {REGISTRY_SINGLETON} from '../../scenes/Registry';
import {MAX_PLAYBACK_SPEED_MULTIPLIER} from '../../configs/playbackSpeed';

const MINUS_SIGN = '−';

// Индекс-based слайдер, а не value-based: обычный min/max/step дал бы
// диапазону 0.1-1 непропорционально маленькую долю ширины трека против
// целых значений (у линейного input значение 0.1 и значение, скажем, 19
// отстоят друг от друга почти на всю длину трека). Здесь каждое соседнее
// значение — это ровно один шаг индекса, поэтому отрезок 0.1-1 (9 шагов) и
// отрезок 1-MAX_PLAYBACK_SPEED_MULTIPLIER занимают на треке
// пропорциональную своему числу шагов ширину. Целая часть строится от общего
// с демо-витриной главного меню потолка скорости (configs/playbackSpeed.js)
const SPEED_STEPS = [
  0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9,
  ...Array.from({length: MAX_PLAYBACK_SPEED_MULTIPLIER}, (_, index) => index + 1),
];

const formatSpeedLabel = (value) => `×${Number.isInteger(value) ? value : value.toFixed(1)}`;

const speedToStepIndex = (speed) => {
  let closestIndex = 0;
  let closestDiff = Infinity;
  SPEED_STEPS.forEach((step, index) => {
    const diff = Math.abs(step - speed);
    if (diff < closestDiff) {
      closestDiff = diff;
      closestIndex = index;
    }
  });
  return closestIndex;
};

/**
 * Панель управления воспроизведением записи матча: перемотка к началу/шаг
 * назад/пауза/шаг вперед + слайдер скорости. Видимость переключается двойным
 * кликом по CompletePhaseButton (ReplayControlsContext) вместе с кнопками
 * боковых панелей — сама панель не решает, когда ей появляться, кроме этого флага.
 *
 * Шаг назад копится (см. accumulateStepBack/STEP_BACK_DEBOUNCE_MS в
 * ReplayControlsContext) — перемотка стартует только когда клики закончились,
 * кнопка в это время показывает накопленный счетчик, а во время самой
 * перемотки (тяжелая синхронная работа в ReplayRunner) — round.png, и вся
 * панель блокируется, чтобы не копить встречные команды поверх перемотки.
 * Перемотка к началу того же типа, но без накопления — там всегда один и тот
 * же результат (позиция 0) независимо от числа кликов
 */
const ReplayPlaybackPanel = () => {
  const {
    isPanelsVisible,
    isPaused,
    togglePaused,
    speedMultiplier,
    setSpeedMultiplier,
    requestStepForward,
    accumulateStepBack,
    requestJumpToStart,
    pendingStepBackCount,
    isRewinding,
  } = useReplayControls();

  if (!isPanelsVisible) {
    return null;
  }

  const withClickSound = (handler) => (event) => {
    event.stopPropagation();
    REGISTRY_SINGLETON.getSoundManager().playMenuButtonClick();
    handler();
  };

  const handleSpeedChange = (event) => {
    setSpeedMultiplier(SPEED_STEPS[Number(event.target.value)]);
  };

  return (
    <div className={`replay-playback-panel ${isRewinding ? 'replay-playback-panel_disabled' : ''}`}>
      <div className="replay-playback-panel-buttons">
        <button
          className="replay-playback-panel-button"
          onClick={withClickSound(requestJumpToStart)}
          disabled={isRewinding}
        >
          {isRewinding ? (
            <img className="replay-playback-panel-spin" src="/img/icons/round.png" alt="Перемотка"/>
          ) : (
            <span className="replay-playback-panel-double-icon">
              <img src="/img/icons/arrow left.png" alt=""/>
              <img src="/img/icons/arrow left.png" alt="К началу"/>
            </span>
          )}
        </button>

        {/* Шаг назад/вперед — только на паузе: до нее эти же тики может
        одновременно диспатчить основной цикл автовоспроизведения
        (ReplayRunner.tick), и клик риск гонки с ним. Кнопка "к началу" выше
        этому не подвержена (полный сброс, не зависит от текущей позиции) */}
        <button
          className="replay-playback-panel-button"
          onClick={withClickSound(accumulateStepBack)}
          disabled={isRewinding || !isPaused}
        >
          {isRewinding ? (
            <img className="replay-playback-panel-spin" src="/img/icons/round.png" alt="Перемотка"/>
          ) : pendingStepBackCount > 0 ? (
            <span className="replay-playback-panel-count">{MINUS_SIGN}{pendingStepBackCount}</span>
          ) : (
            <img src="/img/icons/arrow left.png" alt="Шаг назад"/>
          )}
        </button>

        <button
          className="replay-playback-panel-button"
          onClick={withClickSound(togglePaused)}
          disabled={isRewinding}
        >
          <img src={isPaused ? '/img/icons/play.png' : '/img/icons/pause.png'} alt={isPaused ? 'Продолжить' : 'Пауза'}/>
        </button>

        <button
          className="replay-playback-panel-button"
          onClick={withClickSound(requestStepForward)}
          disabled={isRewinding || !isPaused}
        >
          <img src="/img/icons/arrow right.png" alt="Шаг вперед"/>
        </button>
      </div>

      <div className="replay-playback-panel-speed volume-slider-container">
        <img
          className="volume-slider-icon replay-playback-panel-speed-icon"
          src="/img/icons/upgrade.png"
          alt="Скорость воспроизведения"
          title="Скорость воспроизведения"
        />
        <input
          type="range"
          min={0}
          max={SPEED_STEPS.length - 1}
          step={1}
          value={speedToStepIndex(speedMultiplier)}
          onChange={handleSpeedChange}
          disabled={isRewinding}
          className="volume-slider"
        />
        <span className="volume-slider-value">{formatSpeedLabel(speedMultiplier)}</span>
      </div>
    </div>
  );
};

export default ReplayPlaybackPanel;
